/**
 * Widget 插件入口文件
 *
 * 负责：
 * - 暴露全局 API (SmartCustomerService)
 * - 初始化 Widget
 * - 管理生命周期
 * - 事件总线集成
 * - 浏览器兼容性检查
 * - 错误处理
 */

import React from 'react';
import { createRoot } from 'react-dom/client';
import { WidgetContainer } from './widget';
import { validateConfig } from './widget/config/config.validator';
import { getEventBus } from './widget/services/events';
import { getWidgetStorage } from './widget/services/storage';
import { checkBrowserCompatibility, getBrowserInfo } from './widget/utils/browser';
import { WidgetError, ErrorCodes, getLogger } from './widget/utils/errors';
import { WidgetErrorBoundary } from './widget/components/ErrorBoundary';
import { getMonitor } from './widget/services/monitor';
import type { WidgetConfig, WidgetAPI, WidgetEvent, EventHandler } from './widget/config/widget.types';
import { setRuntimeApiBaseUrl } from './services/runtimeApiConfig';

// 实例单例
const INSTANCE_KEY = '__smart_customer_service_instance__';

// 扩展 Window 接口
declare global {
  interface Window {
    [INSTANCE_KEY]?: WidgetAPI;
    SmartCustomerService?: {
      init: (config: WidgetConfig) => WidgetAPI;
      version: string;
    };
  }
}

/**
 * Widget 版本
 */
const WIDGET_VERSION = '1.0.0';

/**
 * Widget API 实现类
 */
class WidgetAPIImpl implements WidgetAPI {
  private container: HTMLDivElement | null = null;
  private root: any = null;
  private config: WidgetConfig;
  private eventBus = getEventBus();
  private storage = getWidgetStorage();
  private logger = getLogger();
  private monitor = getMonitor();
  private isDestroyed = false;

  constructor(config: WidgetConfig) {
    // 验证配置
    this.config = validateConfig(config);
    setRuntimeApiBaseUrl(this.config.apiBaseUrl);
    this.logger = getLogger(this.config.debug);
    this.monitor = getMonitor(this.config);

    // 跟踪初始化开始
    this.monitor.trackPerformance('widget_init_start', Date.now());

    // 浏览器兼容性检查
    const browserCheck = checkBrowserCompatibility();
    if (!browserCheck.compatible) {
      throw new WidgetError(
        ErrorCodes.BROWSER_NOT_SUPPORTED,
        browserCheck.reason || '浏览器不支持',
        { supportedFeatures: browserCheck.supportedFeatures }
      );
    }

    // 检查是否已存在实例（单例模式）
    if (window[INSTANCE_KEY]) {
      this.logger.warn('Widget 实例已存在，将复用现有实例');
    }

    this.initialize();
    this.setupEventForwarding();

    // 跟踪加载完成
    if (typeof performance !== 'undefined') {
      window.addEventListener('load', () => {
        this.monitor.trackLoadTime();
      });
    }
  }

  /**
   * 初始化 Widget
   */
  private initialize(): void {
    try {
      this.logger.info('初始化 Widget...', {
        version: WIDGET_VERSION,
        config: this.config
      });

      // 创建容器
      this.container = document.createElement('div');
      this.container.id = 'smart-cs-widget-root';
      document.body.appendChild(this.container);

      // 渲染 Widget（使用错误边界）
      this.root = createRoot(this.container);
      this.root.render(
        React.createElement(
          WidgetErrorBoundary,
          null,
          React.createElement(WidgetContainer, { config: this.config })
        )
      );

      // 保存配置到本地存储
      this.storage.savePreferences({
        themeColor: this.config.themeColor,
        title: this.config.title,
        width: this.config.width,
        height: this.config.height
      });

      // 触发初始化事件
      this.eventBus.emit('initialized', {
        version: WIDGET_VERSION,
        config: this.config,
        browser: getBrowserInfo()
      });

      this.logger.info('Widget 初始化成功');
    } catch (error) {
      this.logger.error('Widget 初始化失败', error as Error);
      throw new WidgetError(
        ErrorCodes.INITIALIZATION_FAILED,
        'Widget 初始化失败',
        { originalError: error }
      );
    }
  }

  /**
   * 设置事件转发
   */
  private setupEventForwarding(): void {
    this.eventBus.on('opened', () => {
      this.logger.debugLog('Widget 已打开');
    });

    this.eventBus.on('closed', () => {
      this.logger.debugLog('Widget 已关闭');
    });

    this.eventBus.on('message:sent', (data) => {
      this.logger.debugLog('消息已发送:', data);
    });

    this.eventBus.on('message:received', (data) => {
      this.logger.debugLog('消息已接收:', data);
    });

    this.eventBus.on('error', (data) => {
      this.logger.error('Widget 错误事件:', data);
    });
  }

  /**
   * 更新配置
   */
  updateConfig(newConfig: Partial<WidgetConfig>): void {
    if (this.isDestroyed) {
      this.logger.warn('Widget 已销毁，无法更新配置');
      return;
    }

    this.logger.info('更新 Widget 配置:', newConfig);

    this.config = { ...this.config, ...newConfig };
    setRuntimeApiBaseUrl(this.config.apiBaseUrl);

    // 保存到本地存储
    this.storage.savePreferences(newConfig);

    // 重新渲染
    if (this.root && this.container) {
      this.root.render(
        React.createElement(
          WidgetErrorBoundary,
          null,
          React.createElement(WidgetContainer, { config: this.config })
        )
      );
    }

    // 触发配置更新事件
    this.eventBus.emit('initialized', { config: this.config });
  }

  /**
   * 打开 Widget
   */
  open(): void {
    if (this.isDestroyed) {
      this.logger.warn('Widget 已销毁，无法打开');
      return;
    }
    this.eventBus.emit('opened');
  }

  /**
   * 关闭 Widget
   */
  close(): void {
    if (this.isDestroyed) {
      this.logger.warn('Widget 已销毁，无法关闭');
      return;
    }
    this.eventBus.emit('closed');
  }

  /**
   * 监听事件
   */
  on(event: WidgetEvent, callback: EventHandler): void {
    this.eventBus.on(event, callback);
  }

  /**
   * 取消监听
   */
  off(event: WidgetEvent, callback: EventHandler): void {
    this.eventBus.off(event, callback);
  }

  /**
   * 销毁 Widget
   */
  destroy(): void {
    if (this.isDestroyed) {
      this.logger.warn('Widget 已销毁，跳过');
      return;
    }

    this.logger.info('销毁 Widget...');

    // 标记为已销毁
    this.isDestroyed = true;

    // 卸载 React 组件
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }

    // 移除容器
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }

    // 清理事件总线
    this.eventBus.clear();

    // 清理存储
    this.storage.clear();

    // 删除全局实例
    if (window[INSTANCE_KEY] === this) {
      delete window[INSTANCE_KEY];
    }

    this.logger.info('Widget 已销毁');
  }
}

/**
 * 初始化 Widget
 *
 * @param config - Widget 配置
 * @returns Widget API 实例
 *
 * @example
 * ```html
 * <script src="widget.js"></script>
 * <script>
 *   const widget = SmartCustomerService.init({
 *     apiBaseUrl: 'https://api.example.com',
 *     themeColor: '#2563EB'
 *   });
 * </script>
 * ```
 */
function init(config: WidgetConfig): WidgetAPI {
  // 检查是否已存在实例
  if (window[INSTANCE_KEY]) {
    const existingApi = window[INSTANCE_KEY];
    console.warn('[SmartCS] Widget 已存在，将更新配置');
    existingApi.updateConfig(config);
    return existingApi;
  }

  // 创建新实例
  const api = new WidgetAPIImpl(config);
  window[INSTANCE_KEY] = api;

  // 页面卸载时清理
  window.addEventListener('beforeunload', () => {
    api.destroy();
  });

  return api;
}

/**
 * 导出全局 API
 */
if (typeof window !== 'undefined') {
  (window as any).SmartCustomerService = {
    init,
    version: WIDGET_VERSION
  };
}

// ES Module 导出
export { init };
export type { WidgetConfig, WidgetAPI, WidgetEvent } from './widget/config/widget.types';
export { checkBrowserCompatibility, getBrowserInfo } from './widget/utils/browser';
export { WidgetError, ErrorCodes } from './widget/utils/errors';
